import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, Loader2, AlertTriangle, Check, Crown } from 'lucide-react';
import { validateAdminInvite, acceptAdminInvite, setAuthToken } from '../services/api';
import { useAuth } from '../hooks/useAuth';

export default function AcceptAdminInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = params.get('token');

  const [stage, setStage] = useState('validating'); // validating | invalid | form | submitting | done
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    if (!token) { setStage('invalid'); setError('Missing invite token in URL.'); return; }
    // Wipe any stale auth state from a previous session on this browser —
    // otherwise the api module sends that stale token on every call. A 401
    // from the backend would trigger the global 401 handler in fetchApi,
    // which redirects to /login and aborts the acceptance flow entirely.
    setAuthToken(null);
    validateAdminInvite(token)
      .then(d => { setInvite(d); setName(d.email.split('@')[0]); setStage('form'); })
      .catch(e => { setStage('invalid'); setError(e?.message || 'Invite link is invalid or has expired.'); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setStage('submitting');
    setError(null);
    try {
      const result = await acceptAdminInvite({ token, password, name: name.trim() });
      // CRITICAL: must call setAuthToken (not localStorage.setItem directly),
      // otherwise the api module's in-memory token variable stays stale.
      // Subsequent refreshUser() would send the OLD token, /auth/refresh
      // would return the wrong user (or 401), and AdminRoute would bounce
      // the new admin back to / showing the trial dashboard.
      if (result?.token) {
        setAuthToken(result.token);
      }
      await refreshUser();
      setStage('done');
      setTimeout(() => navigate('/admin', { replace: true }), 1500);
    } catch (err) {
      setError(err?.message || 'Failed to accept invite.');
      setStage('form');
    }
  };

  if (stage === 'validating') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-dark-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Validating invite…</p>
        </div>
      </div>
    );
  }

  if (stage === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-dark-900">
        <div className="max-w-md w-full bg-dark-800 rounded-2xl p-8 border border-red-500/20 text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-white">Invite link not valid</h1>
          <p className="text-sm text-gray-400 mt-2">{error}</p>
          <p className="text-xs text-gray-500 mt-4">If you believe this is wrong, ask the person who invited you to resend the link.</p>
        </div>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-dark-900">
        <div className="max-w-md w-full bg-dark-800 rounded-2xl p-8 border border-emerald-500/20 text-center">
          <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-white">Welcome to the team</h1>
          <p className="text-sm text-gray-400 mt-2">Redirecting you to the admin center…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-dark-900">
      <div className="max-w-md w-full bg-dark-800 rounded-2xl border border-dark-700/50 overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">You've been invited to administer haythix</h1>
              <p className="text-xs text-white/80 mt-0.5">Invited by {invite.invitedByEmail}</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="bg-dark-700/40 rounded-xl p-4 border border-dark-600/30">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Your roles</p>
            <div className="flex flex-wrap gap-1.5">
              {invite.roleLabels.map(r => (
                <span key={r} className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">{r}</span>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-2">Signing in with <span className="text-white font-medium">{invite.email}</span></p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Display name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Set a password (min 8 chars)</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Confirm password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}

          <button type="submit" disabled={stage === 'submitting'}
            className="w-full btn-primary text-sm flex items-center justify-center gap-2 py-2.5">
            {stage === 'submitting' ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating your account…</> : <><Shield className="w-4 h-4" /> Accept invite + sign in</>}
          </button>

          <p className="text-[10px] text-gray-600 text-center leading-relaxed">
            After signing in, enable Two-Factor Authentication in Settings → Security as your first action. It's the single biggest step you can take to keep an admin account safe.
          </p>
        </form>
      </div>
    </div>
  );
}
